/**
 * Scroll Depth Trigger
 *
 * Opens popup when user scrolls to a specified depth on the page.
 * Supports both percentage-based and pixel-based depth measurement.
 * Smart preloading: preloads when user reaches 50% of the target depth.
 */

import type { Popup } from '../popup';
import type { ScrollDepthUnit } from '../../shared/types/popup';
import { createLogger } from '../../shared/logger';
import { TriggerStateBroadcaster } from './trigger-state-broadcaster';

const logger = createLogger('ScrollDepthTrigger');

export class ScrollDepthTrigger {
	private popup: Popup;
	private targetValue: number;
	private unit: ScrollDepthUnit;
	private hasTriggered = false;
	private hasPreloaded = false;
	private boundScrollHandler: () => void;
	private broadcaster: TriggerStateBroadcaster;

	constructor(
		popup: Popup,
		value: number,
		unit: ScrollDepthUnit = 'percent'
	) {
		logger.log('constructor - value:', value, unit);
		this.popup = popup;
		this.targetValue = Math.max(0, value);
		this.unit = unit;
		this.boundScrollHandler = this.handleScroll.bind(this);
		this.broadcaster = new TriggerStateBroadcaster(
			popup.getFeedId(),
			'scroll_depth',
			100 // Throttle to 100ms for scroll events
		);
	}

	/**
	 * Initialize the trigger
	 */
	public init(): void {
		logger.log('init() called, target:', this.targetValue, this.unit);

		if (this.targetValue <= 0) {
			logger.log('Value is <= 0, triggering immediately');
			this.broadcaster.setState('triggered');
			this.popup.show();
			return;
		}

		// Broadcast initial waiting state with target info
		this.broadcaster.setState('waiting');
		this.broadcaster.setProgress({
			currentValue: 0,
			targetValue: this.targetValue,
			unit: this.unit,
		});

		window.addEventListener('scroll', this.boundScrollHandler, {
			passive: true,
		});

		// Check initial scroll position in case user has already scrolled
		this.handleScroll();
	}

	/**
	 * Destroy the trigger
	 */
	public destroy(): void {
		logger.log('destroy() called');
		this.broadcaster.destroy();
		window.removeEventListener('scroll', this.boundScrollHandler);
	}

	/**
	 * Handle scroll events
	 */
	private handleScroll(): void {
		if (this.hasTriggered) {
			return;
		}

		const currentScroll = this.getCurrentScrollValue();
		const preloadThreshold = this.targetValue * 0.5;

		// Broadcast current scroll progress
		this.broadcaster.setProgress({
			currentValue: currentScroll,
			targetValue: this.targetValue,
			unit: this.unit,
		});

		// Preload at 50% of target depth
		if (!this.hasPreloaded && currentScroll >= preloadThreshold) {
			logger.log(
				'Preload threshold reached at',
				currentScroll,
				this.unit
			);
			this.hasPreloaded = true;
			this.broadcaster.setState('preloading');
			this.popup.preload();
		}

		// Trigger at target depth
		if (currentScroll >= this.targetValue) {
			logger.log(
				'Target depth reached!',
				currentScroll,
				'>=',
				this.targetValue,
				this.unit
			);
			this.hasTriggered = true;
			this.broadcaster.setState('triggered');
			this.destroy(); // Remove listener
			this.popup.show();
		}
	}

	/**
	 * Get the current scroll value based on the configured unit.
	 * For 'percent': returns 0-100 representing percentage scrolled
	 * For 'pixels': returns the number of pixels scrolled from top
	 */
	private getCurrentScrollValue(): number {
		const scrollTop = window.scrollY || document.documentElement.scrollTop;

		if (this.unit === 'pixels') {
			return scrollTop;
		}

		// Percentage mode
		const scrollHeight =
			document.documentElement.scrollHeight - window.innerHeight;

		if (scrollHeight <= 0) {
			return 100; // Page fits in viewport, consider it 100% scrolled
		}

		return (scrollTop / scrollHeight) * 100;
	}
}
